import { useEffect, useState } from 'react';
import { api, type SubscriptionView } from '../api.ts';
import { APP_STORE_URL, managedWhere, planSource, PLAY_URL, TIER_NAME } from '../self-service.ts';

/**
 * Your plan — the phone's Profile → Your plan (apps/mobile AccountPages
 * `Subscription`), on the website.
 *
 * What this account has, where it came from (Google Play, the App Store, a
 * gift from DrBell, a care home), when it renews or ends, and what it
 * includes — the lines the server derives from the version they bought, so
 * nothing here decides what a plan includes.
 *
 * No checkout: plans are bought in the app, through the store. The page
 * says so, and says honestly which store: Google Play now; the iPhone app
 * is not on the App Store yet.
 */
export function Plan({ onClose }: { onClose: () => void }) {
  const [data, setData] = useState<SubscriptionView | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    api.get<SubscriptionView>('/api/billing/subscription').then(setData).catch(() => setFailed(true));
  }, []);

  const sub = data?.subscription ?? null;
  const paying = data !== null && data.tier !== 'free' && sub !== null;
  const date = (seconds: number | null) =>
    seconds ? new Date(seconds * 1000).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
  const source = data ? planSource(data.tier, sub?.store) : null;
  const managed = managedWhere(sub?.store);

  return (
    <section aria-labelledby="plan-title">
      <div className="flex items-start gap-3">
        <h2 id="plan-title" className="flex-1 text-xl font-semibold">Your plan</h2>
        <button onClick={onClose} className="min-h-11 rounded-lg border border-(--line) px-3 text-sm">Close</button>
      </div>

      {failed ? (
        // Not knowing is not worth alarm: every reminder works regardless of billing.
        <p className="mt-3 text-sm text-(--muted)">
          Your plan could not be loaded just now. Nothing is wrong with your account, and every reminder keeps working.
        </p>
      ) : !data ? (
        <p className="mt-3 text-sm text-(--muted)">Loading…</p>
      ) : (
        <>
          <p className="mt-3 text-2xl font-semibold">{TIER_NAME[data.tier]}</p>
          {source && <p className="text-sm text-(--muted)">{source}</p>}

          {paying && sub.currentPeriodEnd && (
            <p className="mt-2 text-sm">
              {sub.cancelAtPeriodEnd || sub.store === 'complimentary'
                ? `Ends on ${date(sub.currentPeriodEnd)}. Every reminder keeps working.`
                : `Renews on ${date(sub.currentPeriodEnd)}`}
            </p>
          )}
          {/* A payment in trouble keeps every reminder working, and saying so is the point. */}
          {paying && sub.status === 'past_due' && (
            <p className="mt-2 rounded-lg border border-(--brand) bg-(--brand-soft) px-3 py-2 text-sm">
              The last payment did not go through. Nothing has stopped — every reminder still works while it is sorted out.
            </p>
          )}

          <h3 className="mt-5 text-xs font-semibold uppercase tracking-wide text-(--muted)">What it includes</h3>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
            {data.lines.map((line) => <li key={line.key}>{line.text}</li>)}
          </ul>
          {sub?.keepsOlderTerms && (
            <p className="mt-2 text-xs text-(--muted)">
              You are on plan version {sub.planVersion ?? 1}. If we change what a plan includes, you keep what you bought.
            </p>
          )}
          {managed && <p className="mt-3 text-sm text-(--muted)">{managed}</p>}

          <div className="mt-5 rounded-xl border border-(--line) p-4 text-sm">
            <p className="font-medium">{paying ? 'Changing your plan' : 'Paying for more'}</p>
            <p className="mt-1 text-(--muted)">
              Unlimited medicines, alarms that keep ringing, and everything in the app cost nothing. Paying is for
              reaching the people who look after you.
            </p>
            <p className="mt-2 text-(--muted)">
              Buy a plan here on the website, or in the DrBell app — through Google Play on Android, through the
              App Store on iPhone: <strong className="text-(--fg)">Profile → Your plan</strong>.
            </p>
            <a
              href="/plans"
              className="mr-2 mt-3 inline-flex min-h-11 items-center rounded-lg bg-(--accent) px-4 font-medium text-white hover:opacity-90"
            >
              {paying ? 'Manage your plan' : 'See plans and prices'}
            </a>
            <a
              href={PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mr-2 mt-3 inline-flex min-h-11 items-center rounded-lg border border-(--line) px-4 font-medium hover:border-(--accent)"
            >
              DrBell on Google Play
            </a>
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex min-h-11 items-center rounded-lg border border-(--line) px-4 font-medium hover:border-(--accent)"
            >
              DrBell on the App Store
            </a>
            <p className="mt-3 text-xs text-(--muted)">
              A plan belongs to the account that set a record up: the people you help are on their own family&rsquo;s plan.
            </p>
          </div>
        </>
      )}
    </section>
  );
}
